import { format, parseISO } from 'date-fns';
import { ChevronRight, Plus, Truck, Warehouse } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { MobileFormPage } from '@/components/MobileFormPage';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { fmtFecha, fmtInt } from '@/lib/format';
import { Decimal } from '@/lib/offline/decimal';
import { useEntityId } from '@/lib/offline/operations';
import { useCamada } from '@/lib/queries/camadas';
import { useEntradasAnimales, useGuardarEntrada } from '@/lib/queries/entradasAnimales';
import { useLotesDeCamada } from '@/lib/queries/lotesNave';
import { useAuthStore } from '@/stores/auth';

export function EntradasAnimalesPage() {
  const { camadaId: param } = useParams();
  const id = useEntityId(param, 'camadas');
  const camadaQ = useCamada(id),
    entriesQ = useEntradasAnimales(id),
    lotesQ = useLotesDeCamada(id);
  const [params] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canEdit =
    (user?.rol === 'admin' || user?.rol === 'ganadero') && camadaQ.data && !camadaQ.data.cerrada;
  const entries = entriesQ.data;
  const received = entries?.reduce((sum, e) => sum + e.num_animales, 0);
  const weight = entries?.reduce((sum, e) => sum.add(e.kg_totales), new Decimal(0));
  const assigned = lotesQ.data?.reduce((sum, l) => sum + l.num_animales_asignados, 0);
  const difference = received != null && assigned != null ? received - assigned : null;
  const sorted = entries?.slice().sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id);
  const shown = params.has('todas') ? sorted : sorted?.slice(0, 3);
  return (
    <main className="cm-form-page mx-auto max-w-md px-4">
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent backTo={`/camadas?camada=${id}`} backLabel="Camadas" />
      </header>
      <h1 className="ui-page-title">Llegadas de camiones</h1>
      <p className="cm-form-context">{camadaQ.data?.codigo_lote ?? 'Camada'}</p>
      {!camadaQ.data && (
        <p role={camadaQ.isError ? 'alert' : 'status'}>
          {camadaQ.isError ? extractApiMessage(camadaQ.error) : 'Cargando camada…'}
        </p>
      )}
      <section className="cm-arrival-totals" aria-label="Balance de entradas">
        <dl>
          <div>
            <dt>Recibidos</dt>
            <dd>{received == null ? '—' : fmtInt(received)}</dd>
          </div>
          <div>
            <dt>Asignados</dt>
            <dd>{assigned == null ? '—' : fmtInt(assigned)}</dd>
          </div>
        </dl>
        <p>
          {weight == null
            ? 'Peso sin actualizar'
            : `${fmtInt(weight.toNumber())} kg recibidos en ${entries!.length} ${entries!.length === 1 ? 'llegada' : 'llegadas'}`}
        </p>
      </section>
      {canEdit && (
        <Link className="ui-primary" to={`/camadas/${id}/entradas-animales/nueva`}>
          <Truck size={20} aria-hidden />
          Registrar llegada de camión
          <Plus size={18} aria-hidden />
        </Link>
      )}
      {entries == null ? (
        <p role={entriesQ.isError ? 'alert' : 'status'}>
          {entriesQ.isError ? extractApiMessage(entriesQ.error) : 'Cargando llegadas…'}
        </p>
      ) : entries.length === 0 ? (
        <p className="cm-form-note">
          Sin llegadas registradas. Añade cada camión cuando llegue; cada documento conserva su
          registro.
        </p>
      ) : (
        <ul className="cm-arrival-list">
          {shown!.map((e) => (
            <li key={e.id}>
              <Link
                className="ui-menu-row cm-arrival-row"
                to={`/camadas/${id}/entradas-animales/${e.id}/editar`}
              >
                <Truck size={22} aria-hidden />
                <span>
                  <strong>{e.num_documento_traslado || 'Sin documento'}</strong>
                  <small>
                    {fmtFecha(e.fecha)} · {fmtInt(e.num_animales)} animales
                  </small>
                  <small>
                    {fmtInt(Number(e.kg_totales))} kg
                    {e.local_pending || e.id < 0 ? ' · Pendiente de sincronizar' : ''}
                  </small>
                </span>
                <ChevronRight size={18} aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {entries && entries.length > 3 && !params.has('todas') && (
        <Link className="ui-secondary" to={`?todas=1`}>
          Ver todas las llegadas ({entries.length})
        </Link>
      )}
      <section className="cm-form-note">
        <h2>Asignación a naves</h2>
        <p className="cm-form-balance" role="status">
          {difference == null
            ? 'Balance pendiente de actualizar.'
            : !entries?.length
              ? 'Todavía no hay llegadas registradas. Puedes preparar las naves.'
              : difference === 0
                ? 'Recibidos y asignados coinciden.'
                : difference > 0
                  ? `${fmtInt(difference)} ${difference === 1 ? 'animal pendiente' : 'animales pendientes'} de asignar.`
                  : `${fmtInt(-difference)} ${difference === -1 ? 'animal asignado' : 'animales asignados'} por encima de los recibidos. Revisa las cantidades.`}
        </p>
        {canEdit && (
          <Link className="ui-secondary mt-3" to={`/camadas/${id}/asignar-lotes`}>
            <Warehouse size={20} aria-hidden />
            {lotesQ.data?.length ? 'Revisar asignación' : 'Preparar naves'}
          </Link>
        )}
        <p>Las llegadas pertenecen a la camada. Cada grupo asignado permanece en su nave.</p>
      </section>
    </main>
  );
}

type Draft = { documento: string; fecha: string; animales: string; peso: string };
export function EntradaAnimalesFormPage() {
  const { camadaId: param, entradaId: entryParam } = useParams();
  const id = useEntityId(param, 'camadas'),
    entryId = useEntityId(entryParam, 'entradas');
  const edit = entryParam != null;
  const camadaQ = useCamada(id),
    entriesQ = useEntradasAnimales(id),
    saveQ = useGuardarEntrada(id);
  const user = useAuthStore((s) => s.user),
    owner = user?.id;
  const canEdit =
    (user?.rol === 'admin' || user?.rol === 'ganadero') && camadaQ.data && !camadaQ.data.cerrada;
  const entry = entriesQ.data?.find((e) => e.id === entryId);
  const [draft, setDraft] = useState<Draft | null>(null),
    [baseline, setBaseline] = useState<Draft | null>(null);
  const dirty =
    draft != null &&
    baseline != null &&
    Object.keys(draft).some((key) => draft[key as keyof Draft] !== baseline[key as keyof Draft]);
  const ready = !!camadaQ.data && entriesQ.data != null && (!edit || !!entry);
  useEffect(() => {
    setDraft(null);
    setBaseline(null);
  }, [param, entryParam, owner]);
  useEffect(() => {
    if (!ready || dirty || !camadaQ.data) return;
    const values = entry
      ? {
          documento: entry.num_documento_traslado ?? '',
          fecha: entry.fecha,
          animales: String(entry.num_animales),
          peso: String(entry.kg_totales),
        }
      : { documento: '', fecha: format(new Date(), 'yyyy-MM-dd'), animales: '', peso: '' };
    setDraft(values);
    setBaseline(values);
  }, [ready, entry, camadaQ.data, dirty]);
  function field(key: keyof Draft, value: string) {
    setDraft((previous) => (previous ? { ...previous, [key]: value } : previous));
  }
  const backTo = `/camadas/${id}/entradas-animales`;
  async function save() {
    if (!draft || !ready || !canEdit || (edit && !dirty)) return;
    const document = draft.documento.trim(),
      quantity = Number(draft.animales),
      kg = draft.peso.trim().replace(',', '.');
    if (!document || document.length > 100)
      throw new Error('Introduce el documento de traslado de este camión, hasta 100 caracteres.');
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(draft.fecha) ||
      Number.isNaN(parseISO(draft.fecha).getTime()) ||
      draft.fecha < camadaQ.data!.fecha_entrada
    )
      throw new Error('La llegada debe tener una fecha válida desde el inicio de la camada.');
    if (!/^\d+$/.test(draft.animales) || !Number.isSafeInteger(quantity) || quantity <= 0)
      throw new Error('Introduce un número entero de animales mayor que cero.');
    if (!/^\d{1,10}(?:\.\d{1,2})?$/.test(kg))
      throw new Error(
        'Introduce el peso total en kg, con hasta dos decimales. Introduce 0 si aún no lo conoces.',
      );
    if (
      entriesQ.data!.some(
        (e) => e.id !== entryId && e.fecha === draft.fecha && e.num_documento_traslado === document,
      )
    )
      throw new Error(
        'Ya existe una llegada con este documento y fecha. Edita la llegada existente.',
      );
    await saveQ.mutateAsync({
      ...(edit ? { id: entryId! } : {}),
      data: {
        fecha: draft.fecha,
        num_animales: quantity,
        kg_totales: new Decimal(kg).toString(),
        num_documento_traslado: document,
      },
    });
    if (useAuthStore.getState().user?.id !== owner) return;
    toast.show({
      kind: 'success',
      message: 'Llegada guardada.',
    });
    return backTo;
  }
  const toast = useToast();
  return (
    <MobileFormPage
      title={edit ? 'Editar llegada de camión' : 'Llegada de camión'}
      backTo={backTo}
      saveLabel={edit ? 'Guardar cambios' : 'Guardar llegada'}
      dirty={dirty}
      disabled={!canEdit || !ready || !draft || (edit && !dirty)}
      onSave={save}
    >
      <p className="cm-form-context">
        {camadaQ.data?.codigo_lote ?? 'Camada'}{' '}
        <span>{edit ? 'Editar esta llegada' : 'Una llegada por documento'}</span>
      </p>
      {!ready && (
        <p
          role={
            camadaQ.isError || entriesQ.isError || (edit && entriesQ.data != null && !entry)
              ? 'alert'
              : 'status'
          }
        >
          {camadaQ.isError || entriesQ.isError
            ? 'Llegada no disponible. Conecta para actualizar.'
            : edit && entriesQ.data != null && !entry
              ? 'Esta llegada no pertenece a la camada.'
              : 'Cargando llegada…'}
        </p>
      )}
      {ready && (
        <>
          {!canEdit && (
            <p className="cm-form-note">
              {camadaQ.data!.cerrada
                ? 'La camada está cerrada. Puedes consultar esta llegada.'
                : 'Tu perfil permite consultar esta llegada.'}
            </p>
          )}
          <div className="cm-form-field">
            <label htmlFor="arrival-doc">Documento de traslado</label>
            <input
              id="arrival-doc"
              className="ui-field"
              value={draft?.documento ?? ''}
              maxLength={100}
              autoComplete="off"
              disabled={!canEdit}
              onChange={(e) => field('documento', e.target.value)}
            />
            <p>Documento de este camión. Cada llegada conserva el suyo.</p>
          </div>
          <div className="cm-form-field">
            <label htmlFor="arrival-date">Fecha de llegada</label>
            <input
              id="arrival-date"
              className="ui-field"
              type="date"
              min={camadaQ.data!.fecha_entrada}
              value={draft?.fecha ?? ''}
              disabled={!canEdit}
              onChange={(e) => field('fecha', e.target.value)}
            />
          </div>
          <div className="cm-form-field">
            <label htmlFor="arrival-animals">Animales de este camión</label>
            <input
              id="arrival-animals"
              className="ui-field"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft?.animales ?? ''}
              disabled={!canEdit}
              onChange={(e) => field('animales', e.target.value)}
            />
          </div>
          <div className="cm-form-field">
            <label htmlFor="arrival-weight">Peso total de este camión · kg</label>
            <input
              id="arrival-weight"
              className="ui-field"
              inputMode="decimal"
              value={draft?.peso ?? ''}
              disabled={!canEdit}
              onChange={(e) => field('peso', e.target.value)}
            />
            <p>Introduce 0 si todavía no conoces el peso.</p>
          </div>
          <p className="cm-form-note">
            {edit
              ? 'Guardar cambia solo esta llegada. Las demás conservan sus datos.'
              : 'Una llegada nueva se suma a las anteriores. Puedes registrar varios camiones el mismo día.'}
          </p>
        </>
      )}
    </MobileFormPage>
  );
}
